/**
 * Partners — organisations we work with, as distinct from ones we sell to.
 *
 * Built on the ambassador screen's pattern because they are the same kind of
 * thing: a relationship record with contact details, a deal, and links that are
 * maintained elsewhere. Same list conventions as the other three screens —
 * search, status chips, a Table/Kanban toggle, a row that opens the editor.
 *
 * Not in the original design. The handoff's Marketing section has four tabs; a
 * fifth was added deliberately, and placed last so the stated grouping still
 * holds: the two owned-channel screens, then the relationship screens.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  subscribeToPartners,
  createPartner,
  updatePartner,
  deletePartner,
} from './repository/partnersRepository'
import {
  EMPTY_PARTNER,
  PARTNER_STATUSES,
  partnerStatusStyle,
  type Partner,
} from '@/types/partners'
import { ListMap, type MapRecord } from '@/shared/map/LazyListMap'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { ColumnPicker, DataTable } from '@/shared/components/DataTable'
import type { Cell, ColumnMeta, DataRow, FilterOptions } from '@/shared/components/DataTable'
import { useTableState } from '@/shared/hooks/useTableState'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import { haystack, matchesTokens } from '@/shared/util/tokenSearch'
import { useAuth } from '@/features/auth/AuthProvider'
import { PartnerModal } from './PartnerModal'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import './PartnersWorkspace.css'

/**
 * The partner's mark, or their initials.
 *
 * The logo is served from the partner's own site, so it can 404 when they
 * redesign. A broken image frame in a table reads as a bug in our tool rather
 * than as a change on their end, so a failure falls back to initials — the same
 * treatment leads already get.
 */
function PartnerLogo({ partner }: { partner: Partner }) {
  const [ok, setOk] = useState(true)
  const initials = partner.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('')

  if (!partner.logoUrl || !ok) {
    return <span className="ptn__logo ptn__logo--fallback">{initials}</span>
  }
  return (
    <img
      className="ptn__logo"
      src={partner.logoUrl}
      alt=""
      loading="lazy"
      onError={() => setOk(false)}
    />
  )
}

/**
 * The columns, and how wide each one gets.
 *
 * Partners had a hand-rolled grid while leads and events shared `DataTable`.
 * Moving it over brings column picking, per-column filters, sorting and the
 * logo slot for free, and means the three list screens keep behaving the same
 * way as that component changes — which a fourth copy of the markup would not.
 */
const COLUMN_META: Record<ColumnKey, ColumnMeta> = {
  kind: { label: 'Kind', width: 0.8 },
  status: { label: 'Status', width: 0.8 },
  country: { label: 'Country', width: 0.8 },
  contact: { label: 'Contact', width: 1.1 },
  arrangement: { label: 'Arrangement', width: 2 },
  campaignCode: { label: 'Campaign code', width: 0.9 },
}

type ColumnKey = 'kind' | 'status' | 'country' | 'contact' | 'arrangement' | 'campaignCode'

const ALL_COLUMNS: ColumnKey[] = ['kind', 'status', 'country', 'contact', 'arrangement', 'campaignCode']
/** The design's six, minus country — shown by default, pickable by anyone. */
const DEFAULT_COLUMNS: ColumnKey[] = ['kind', 'status', 'contact', 'arrangement', 'campaignCode']
const FILTERABLE: ColumnKey[] = ['kind', 'status', 'country']

export function PartnersWorkspace() {
  const { user } = useAuth()
  const currentUser = user?.displayName ?? ''

  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState<Partner['status'] | 'all'>('all')
  const [viewMode, setViewMode] = useViewMode()
  const isMobile = useIsMobile()
  const t = useTableState<ColumnKey>({
    storageKey: 'crm.partners.columns',
    allKeys: ALL_COLUMNS,
    defaults: DEFAULT_COLUMNS,
    initialSort: 'name',
  })
  const [editing, setEditing] = useState<Partner | 'new' | null>(null)

  useEffect(() => {
    const un = subscribeToPartners(
      (p) => {
        setPartners(p)
        setLoading(false)
      },
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
    return un
  }, [])

  const rows = useMemo(
    () =>
      partners
        .filter((p) => status === 'all' || p.status === status)
        // Same shape as the events screen: a column with nothing selected
        // means "all", so an empty selection must not exclude everything.
        .filter((p) =>
          FILTERABLE.every((key) => {
            const selected = t.filters[key]
            if (!selected?.length) return true
            return selected.includes(key === 'kind' ? p.kind : key === 'status' ? p.status : p.country)
          }),
        )
        .filter((p) =>
          matchesTokens(term, (token) =>
            haystack([
              p.name,
              p.kind,
              p.country,
              p.contactName,
              p.email,
              p.arrangement,
              p.campaignCode,
              p.owner,
            ]).includes(token),
          ),
        ),
    [partners, status, term, t.filters],
  )

  /**
   * The designer's four, in their order. "Campaign codes" counts partners who
   * have one rather than codes in existence — a partner with no code is a
   * partnership nothing can be measured through, which is the point of showing
   * it beside the total.
   */
  const kpis = useMemo(
    () => [
      { label: 'Total partners', value: partners.length },
      { label: 'Active', value: partners.filter((p) => p.status === 'active').length },
      { label: 'In conversation', value: partners.filter((p) => p.status === 'talking').length },
      { label: 'Campaign codes', value: partners.filter((p) => p.campaignCode.trim()).length },
    ],
    [partners],
  )

  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of partners) m.set(p.status, (m.get(p.status) ?? 0) + 1)
    return m
  }, [partners])

  /** The logo sits in DataTable's `leading` slot, as the lead logo does. */
  const dataRows: DataRow<ColumnKey>[] = useMemo(
    () =>
      rows.map((p) => {
        const st = partnerStatusStyle(p.status)
        const cell = (key: ColumnKey, text: string, extra?: Partial<Cell<ColumnKey>>): Cell<ColumnKey> => ({
          key,
          badge: false,
          text,
          title: text,
          ...extra,
        })
        const byKey: Record<ColumnKey, Cell<ColumnKey>> = {
          kind: cell('kind', p.kind),
          status: { key: 'status', badge: true, text: st.label, bg: st.bg, color: st.color },
          country: cell('country', p.country || '—'),
          contact: cell('contact', p.contactName || p.email || 'nobody named'),
          arrangement: cell('arrangement', p.arrangement || '—'),
          campaignCode: cell('campaignCode', p.campaignCode || '—'),
        }
        return {
          id: p.id,
          title: p.name,
          subtitle: [p.country, p.website && hostOf(p.website)].filter(Boolean).join(' · '),
          leading: <PartnerLogo partner={p} />,
          cells: t.columns.map((k) => byKey[k]),
          // The phone card names the kind and who to write to; the subtitle
          // above it already carries the country and the site.
          card: {
            status: { label: st.label, bg: st.bg, color: st.color },
            lines: [p.kind, p.contactName || p.email || 'nobody named'],
          },
        }
      }),
    [rows, t.columns],
  )

  /** Only values actually present, so a filter never offers an empty result. */
  const filterOptions: FilterOptions<ColumnKey> = useMemo(
    () => ({
      kind: [...new Set(partners.map((p) => p.kind))].sort().map((v) => ({ value: v, label: v })),
      status: PARTNER_STATUSES.filter((s) => partners.some((p) => p.status === s.key)).map((s) => ({
        value: s.key,
        label: s.label,
      })),
      country: [...new Set(partners.map((p) => p.country).filter(Boolean))]
        .sort()
        .map((v) => ({ value: v, label: v })),
    }),
    [partners],
  )

  const save = async (draft: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => {
    if (id) await updatePartner(id, draft)
    else await createPartner(draft)
    setEditing(null)
  }

  /**
   * The filtered partners, for the map.
   *
   * A partner record holds a country and rarely more, so every one of these is
   * a country centroid — a hollow pin over the middle of Norway rather than an
   * address. That is what the data supports, and the pin says so.
   */
  const mapRecords = useMemo(
    (): MapRecord[] =>
      rows.map((p) => {
        const st = partnerStatusStyle(p.status)
        return {
          id: p.id,
          title: p.name,
          subtitle: [p.kind, p.country].filter(Boolean).join(' · '),
          color: st.color,
          statusLabel: st.label,
          geo: p.geo,
          missingReason: p.country ? 'could not be placed' : 'no country on the record',
        }
      }),
    [rows],
  )

  return (
    <main className="ptn">
      <div className="ptn__head">
        <div>
          <h2 className="ptn__title">Partners</h2>
          <p className="ptn__sub">
            {loading
              ? 'Loading…'
              : partners.length === 0
                ? 'Nobody here yet — organisations we work with rather than sell to.'
                : `${partners.length} partners · ${rows.length} shown`}
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => setEditing('new')}>
          + New partner
        </button>
      </div>

      {error && <div className="empty-note">Could not read partners: {error}</div>}

      <div className="ptn__kpis">
        {kpis.map((k) => (
          <div className="ptn__kpi" key={k.label}>
            <div className="ptn__kpilabel">{k.label}</div>
            <div className="ptn__kpivalue">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="ptn__filters">
        <input
          className="input ptn__search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Name, kind, country, contact, code…"
          aria-label="Search partners"
        />
        <button className="chip" aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
          All
        </button>
        {PARTNER_STATUSES.map((s) => (
          <button
            key={s.key}
            className="chip"
            aria-pressed={status === s.key}
            onClick={() => setStatus(s.key)}
          >
            {s.label} ({counts.get(s.key) ?? 0})
          </button>
        ))}
        {!isMobile && (
          <div className="ptn__cols">
            <button className="btn btn--ghost" onClick={() => t.setShowPicker((v) => !v)} aria-expanded={t.showPicker}>
              ⚙ Columns
            </button>
            {t.showPicker && (
              <ColumnPicker
                meta={COLUMN_META}
                columns={t.columns}
                onToggle={t.toggleColumn}
                onClose={() => t.setShowPicker(false)}
              />
            )}
          </div>
        )}
        <ViewToggle value={viewMode} onChange={setViewMode} label="Partners" withMap />
      </div>

      {viewMode === 'map' ? (
        <ListMap
          records={mapRecords}
          onOpen={(id) => setEditing(partners.find((p) => p.id === id) ?? null)}
          emptyNote="No partner matches these filters, so there is nothing to place."
        />
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          columns={PARTNER_STATUSES.map((s) => ({ key: s.key, label: s.label, color: s.color }))}
          cards={rows.map(toCard)}
          onSelect={(id) => setEditing(partners.find((p) => p.id === id) ?? null)}
          emptyNote={loading ? 'Loading…' : 'No partner matches these filters. Clear a filter to see everyone we work with.'}
        />
      ) : (
        <DataTable
          meta={COLUMN_META}
          columns={t.columns}
          rows={dataRows}
          identityLabel="Partner"
          identitySortKey="name"
          sortBy={t.sortBy}
          sortDir={t.sortDir}
          onSort={t.onSort}
          filterable={FILTERABLE}
          filters={t.filters}
          filterOptions={filterOptions}
          onToggleFilter={t.toggleFilter}
          onClearFilter={t.clearFilter}
          openFilter={t.openFilter}
          onOpenFilter={t.setOpenFilter}
          selectedId={editing && editing !== 'new' ? editing.id : null}
          onSelect={(id) => setEditing(partners.find((p) => p.id === id) ?? null)}
          loading={loading}
          loadingNote="Loading partners…"
          emptyNote={
            partners.length === 0
              ? 'No partners recorded yet. Add the first one to start tracking what each agreement delivers.'
              : 'No partner matches these filters. Clear a filter to see everyone we work with.'
          }
        />
      )}

      {editing && (
        <PartnerModal
          partner={editing === 'new' ? { id: '', ...EMPTY_PARTNER, owner: currentUser } : editing}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={async (id) => {
            await deletePartner(id)
            setEditing(null)
          }}
        />
      )}
    </main>
  )
}

/** Just the host, so the column stays narrow and scannable. */
function hostOf(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] ?? ''
}

function toCard(p: Partner): KanbanCard {
  return {
    id: p.id,
    group: p.status,
    title: p.name,
    meta: [p.kind, p.country].filter(Boolean).join(' · '),
    badge: p.campaignCode
      ? {
          text: p.campaignCode,
          bg: 'var(--color-surface-muted)',
          color: 'var(--color-text-muted)',
        }
      : null,
    trailing: p.owner,
  }
}
